<script lang="ts">
  import { cn } from "$lib/utility";
  import { FileText, Globe, Search, Workflow, Link2, Users, ShoppingCart, FileUser, PlugZap } from "lucide-svelte";
  import { Feather, BookOpen } from "lucide-svelte";
  import { SVG } from "$lib/components";
  import { pricingIcon2, companyIcon } from "$lib/images/icons";

  export let currentShortcutSequence: string | null;
  export let isBlogPage: boolean;
  export let routes: any;
  export let goto: (url: string) => void;
  export let isInShortcutMode: boolean = false;

  let featuresDropdownOpen = false;
  let companyDropdownOpen = false;
  let useCasesDropdownOpen = false;

  let featuresTimeout: number | null = null;
  let companyTimeout: number | null = null;
  let useCasesTimeout: number | null = null;

  const hoverDelay = 350;

  function closeDropdowns() {
    featuresDropdownOpen = false;
    companyDropdownOpen = false;
    useCasesDropdownOpen = false;
  }

  function handleFeaturesEnter() {
    if (featuresTimeout) {
      clearTimeout(featuresTimeout);
      featuresTimeout = null;
    }
    companyDropdownOpen = false;
    useCasesDropdownOpen = false;
    featuresDropdownOpen = true;
  }

  function handleFeaturesLeave() {
    featuresTimeout = setTimeout(() => {
      if (!currentShortcutSequence || !currentShortcutSequence.startsWith("f")) {
        featuresDropdownOpen = false;
      }
      featuresTimeout = null;
    }, hoverDelay) as unknown as number;
  }

  function handleCompanyEnter() {
    if (companyTimeout) {
      clearTimeout(companyTimeout);
      companyTimeout = null;
    }
    featuresDropdownOpen = false;
    useCasesDropdownOpen = false;
    companyDropdownOpen = true;
  }

  function handleCompanyLeave() {
    companyTimeout = setTimeout(() => {
      if (!currentShortcutSequence || !currentShortcutSequence.startsWith("c")) {
        companyDropdownOpen = false;
      }
      companyTimeout = null;
    }, hoverDelay) as unknown as number;
  }

  function handleUseCasesEnter() {
    if (useCasesTimeout) {
      clearTimeout(useCasesTimeout);
      useCasesTimeout = null;
    }
    featuresDropdownOpen = false;
    companyDropdownOpen = false;
    useCasesDropdownOpen = true;
  }

  function handleUseCasesLeave() {
    useCasesTimeout = setTimeout(() => {
      if (!currentShortcutSequence || !currentShortcutSequence.startsWith("u")) {
        useCasesDropdownOpen = false;
      }
      useCasesTimeout = null;
    }, hoverDelay) as unknown as number;
  }

  function navigateTo(url: string) {
    goto(url);
    closeDropdowns();
  }

  $: if (currentShortcutSequence === "f") {
    featuresDropdownOpen = true;
    companyDropdownOpen = false;
    useCasesDropdownOpen = false;
  } else if (currentShortcutSequence === "c") {
    featuresDropdownOpen = false;
    companyDropdownOpen = true;
    useCasesDropdownOpen = false;
  } else if (currentShortcutSequence === "u") {
    featuresDropdownOpen = false;
    companyDropdownOpen = false;
    useCasesDropdownOpen = true;
  }
</script>

<div
  class={cn(
    "left-0 right-0 z-0 hidden items-center justify-center gap-6 text-theme-text-primary dark:text-theme-text-primary lg:flex",
    isBlogPage ? "text-white" : "",
  )}
>
  <div
    class="relative"
    role="button"
    tabindex="0"
    aria-haspopup="true"
    aria-expanded={featuresDropdownOpen}
    aria-controls="features-dropdown"
    onmouseenter={handleFeaturesEnter}
    onmouseleave={handleFeaturesLeave}
    onkeydown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        companyDropdownOpen = false;
        useCasesDropdownOpen = false;
        featuresDropdownOpen = !featuresDropdownOpen;
      } else if (e.key === "Escape" && featuresDropdownOpen) {
        featuresDropdownOpen = false;
      }
    }}
  >
    <div
      role="button"
      id="features-button"
      class="relative flex opacity-60 hover:opacity-100 items-center text-base font-normal transition-color duration-200 gap-2"
    >
      <span class="normal-case font-light">Features</span>
    </div>
    {#if featuresDropdownOpen}
      <div
        id="features-dropdown"
        role="menu"
        tabindex="0"
        aria-labelledby="features-button"
        class="absolute -left-6 z-50 mt-4 w-[320px] overflow-hidden rounded-xl border border-white/10 bg-black/80 backdrop-blur-xl shadow-2xl ring-1 ring-white/5 transition-all duration-200"
        onmouseenter={handleFeaturesEnter}
        onmouseleave={handleFeaturesLeave}
      >
        <div class="p-3">
          <div class="space-y-1">
            <a
              href="/features/ai-web-scraping"
              data-sveltekit-preload-data="hover"
              class="group block rounded-lg px-3 py-2 transition-all duration-200 hover:bg-white/10"
              onclick={() => navigateTo("/features/ai-web-scraping")}
            >
              <div>
                <h4 class="text-sm font-medium text-white/70 transition-colors group-hover:text-white">
                  AI Web Scraping
                </h4>
                <p class="mt-1 text-xs font-normal text-white/60">Gather data from websites automatically</p>
              </div>
            </a>
            <a
              href="/features/ai-pdf-processing"
              data-sveltekit-preload-data="hover"
              class="group block rounded-lg px-3 py-2 transition-all duration-200 hover:bg-white/10"
              onclick={() => navigateTo("/features/ai-pdf-processing")}
            >
              <div>
                <h4 class="text-sm font-medium text-white/70 transition-colors group-hover:text-white">
                  AI PDF Processing
                </h4>
                <p class="mt-1 text-xs font-normal text-white/60">Extract insights from PDF documents</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <div
    class="relative"
    role="button"
    tabindex="0"
    aria-haspopup="true"
    aria-expanded={useCasesDropdownOpen}
    aria-controls="usecases-dropdown"
    onmouseenter={handleUseCasesEnter}
    onmouseleave={handleUseCasesLeave}
    onkeydown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        featuresDropdownOpen = false;
        companyDropdownOpen = false;
        useCasesDropdownOpen = !useCasesDropdownOpen;
      } else if (e.key === "Escape" && useCasesDropdownOpen) {
        useCasesDropdownOpen = false;
      }
    }}
  >
    <div
      role="button"
      id="usecases-button"
      class="relative flex opacity-60 hover:opacity-100 items-center text-base font-normal transition-color duration-200 gap-2"
    >
      <span class="relative">
        <span class="normal-case font-light">Use Cases</span>
      </span>
      <!-- {#if isInShortcutMode && !currentShortcutSequence}
        <div
          class="absolute -bottom-4 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
        >
          <span class="text-xs">U</span>
        </div>
      {/if} -->
    </div>
    {#if useCasesDropdownOpen}
      <div
        id="usecases-dropdown"
        role="menu"
        tabindex="0"
        aria-labelledby="usecases-button"
        class="absolute -left-6 z-50 mt-4 w-[320px] overflow-hidden rounded-xl border border-white/10 bg-black/80 backdrop-blur-xl shadow-2xl ring-1 ring-white/5 transition-all duration-200"
        onmouseenter={handleUseCasesEnter}
        onmouseleave={handleUseCasesLeave}
      >
        <div class="p-3">
          <div class="space-y-1">
            <a
              href="/use-cases/ai-ecommerce-automation"
              data-sveltekit-preload-data="hover"
              class="group block rounded-lg px-3 py-2 transition-all duration-200 hover:bg-white/10"
              onclick={() => navigateTo("/use-cases/ai-ecommerce-automation")}
            >
              <div>
                <h4 class="text-sm font-medium text-white/70 transition-colors group-hover:text-white">
                  E-commerce Competitor Research
                </h4>
                <p class="mt-1 text-xs font-normal text-white/60">Analyze and track competitor strategies</p>
              </div>
            </a>

            <a
              href="/use-cases/recruiting-ai-resume-screening"
              data-sveltekit-preload-data="hover"
              class="group block rounded-lg px-3 py-2 transition-all duration-200 hover:bg-white/10"
              onclick={() => navigateTo("/use-cases/recruiting-ai-resume-screening")}
            >
              <div>
                <h4 class="text-sm font-medium text-white/70 transition-colors group-hover:text-white">
                  AI Resume Screening
                </h4>
                <p class="mt-1 text-xs font-normal text-white/60">Review Resumes 100X Faster with AI</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <a
    href="/integrations"
    data-sveltekit-preload-data="hover"
    class="relative flex opacity-60 hover:opacity-100 items-center text-base font-normal transition-color duration-200 gap-2"
    onclick={(e) => {
      e.preventDefault();
      goto("/integrations");
      closeDropdowns();
    }}
  >
    <span class="normal-case font-light">Integrations</span>
    <!-- {#if isInShortcutMode && !currentShortcutSequence}
      <div
        class="absolute -bottom-4 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
      >
        <span class="text-xs">I</span>
      </div>
    {/if} -->
  </a>

  <!-- <a
    href="/learn"
    class="relative flex opacity-60 hover:opacity-100 items-center text-base font-normal transition-color duration-200 gap-2"
    onclick={(e) => {
      e.preventDefault();
      goto("/learn");
      closeDropdowns();
    }}
  >
    <span class="normal-case font-light">Learn</span>
  </a> -->

  <a
    href={routes.pricing}
    data-sveltekit-preload-data="hover"
    class="relative flex opacity-60 hover:opacity-100 items-center text-base font-normal transition-color duration-200 gap-2"
    onclick={(e) => {
      e.preventDefault();
      goto(routes.pricing);
      closeDropdowns();
    }}
  >
    <span class="normal-case font-light">Pricing</span>
    <!-- {#if isInShortcutMode && !currentShortcutSequence}
      <div
        class="absolute -bottom-4 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
      >
        <span class="text-xs">P</span>
      </div>
    {/if} -->
  </a>

  <!-- <div
    class="relative"
    role="button"
    tabindex="0"
    aria-haspopup="true"
    aria-expanded={companyDropdownOpen}
    aria-controls="company-dropdown"
    onmouseenter={handleCompanyEnter}
    onmouseleave={handleCompanyLeave}
    onkeydown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        featuresDropdownOpen = false;
        useCasesDropdownOpen = false;
        companyDropdownOpen = !companyDropdownOpen;
      } else if (e.key === "Escape" && companyDropdownOpen) {
        companyDropdownOpen = false;
      }
    }}
  >
    <div
      role="button"
      id="company-button"
      class="relative flex items-center text-base font-normal transition-all duration-200
      hover:scale-[1.05] gap-2"
    >
      <SVG file={companyIcon} className="w-4 h-4" />
      COMPANY
      {#if isInShortcutMode && !currentShortcutSequence}
        <div
          class="absolute -bottom-4 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
        >
          <span class="text-xs">C</span>
        </div>
      {/if}
    </div>
    {#if companyDropdownOpen}
      <div
        id="company-dropdown"
        role="menu"
        tabindex="0"
        aria-labelledby="company-button"
        class={cn(
          "absolute left-1/2 z-50 mt-4 grid w-[520px] -translate-x-1/2 grid-cols-2 overflow-hidden rounded-base border border-theme-border-subtle bg-white shadow-sm transition-all duration-200 dark:bg-[#09090B]",
          isBlogPage ? "!border-white/20 bg-[var(--dark-background-tertiary)] dark:!border-white/20" : "",
        )}
        onmouseenter={handleCompanyEnter}
        onmouseleave={handleCompanyLeave}
      >
        <div class={cn("border-r p-6 border-theme-border-subtle", isBlogPage ? "border-white/20" : "")}>
          <div class="space-y-4">
            <a
              href="/about"
              class={cn(
                "group -m-2 block rounded-base p-2 transition-colors duration-75 hover:bg-gray-100 dark:hover:bg-white/10",
                isBlogPage ? "hover:bg-white/10" : "",
              )}
              onclick={() => navigateTo("/about")}
            >
              <div class="flex items-center gap-2">
                <Users
                  class={cn(
                    "h-5 w-5 stroke-1 text-theme-green-deep dark:text-theme-green",
                    isBlogPage ? "text-theme-green" : "",
                  )}
                />
                <h4
                  class={cn(
                    "mb-1 text-xs font-normal text-gray-900 transition-colors group-hover:text-theme-green-deep dark:text-white dark:group-hover:text-theme-green relative",
                    isBlogPage ? "text-white group-hover:text-theme-green" : "",
                  )}
                >
                  About
                  {#if isInShortcutMode && currentShortcutSequence === "c"}
                    <div
                      class="absolute -right-7 bottom-0 flex size-5 items-center justify-center bg-yellow-400 text-black"
                    >
                      <span class="text-xs">1</span>
                    </div>
                  {/if}
                </h4>
              </div>
              <p
                class={cn(
                  "ml-6 pl-1 text-xs normal-case font-light text-gray-500 dark:text-gray-400",
                  isBlogPage ? "text-gray-400" : "",
                )}
              >
                Learn about our mission
              </p>
            </a>
          </div>
        </div>

        <div class="p-6">
          <div class="space-y-4">
            <a
              href={routes.blog}
              class={cn(
                "group -m-2 block rounded-base p-2 transition-colors duration-75 hover:bg-gray-100 dark:hover:bg-white/10",
                isBlogPage ? "hover:bg-white/10" : "",
              )}
              onclick={() => navigateTo(routes.blog)}
            >
              <div class="flex items-center gap-2">
                <FileText
                  class={cn(
                    "h-5 w-5 stroke-1 text-theme-green-deep dark:text-theme-green",
                    isBlogPage ? "text-theme-green" : "",
                  )}
                />
                <h4
                  class={cn(
                    "mb-1 text-xs font-normal text-gray-900 transition-colors group-hover:text-theme-green-deep dark:text-white dark:group-hover:text-theme-green relative",
                    isBlogPage ? "text-white group-hover:text-theme-green" : "",
                  )}
                >
                  Blog
                  {#if isInShortcutMode && currentShortcutSequence === "c"}
                    <div
                      class="absolute -right-7 bottom-0 flex size-5 items-center justify-center bg-yellow-400 text-black"
                    >
                      <span class="text-xs">2</span>
                    </div>
                  {/if}
                </h4>
              </div>
              <p
                class={cn(
                  "ml-6 pl-1 text-xs normal-case font-light text-gray-500 dark:text-gray-400",
                  isBlogPage ? "text-gray-400" : "",
                )}
              >
                Latest news and insights
              </p>
            </a>
          </div>
        </div>
      </div>
    {/if}
  </div> -->
</div>
